'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { faFileLines } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useAnalyticsContext } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows } from '@b-stock/fe-core/design-system'

import {
  clampNotePopoverLeft,
  NOTE_POPOVER_VIEWPORT_MARGIN,
  NOTE_POPOVER_WIDTH,
  notePopoverWidthCss,
} from '@/components/listings/notes/notePopover'
import {
  NOTES_AMBER_BG,
  NOTES_AMBER_ICON,
} from '@/components/listings/notes/notes-colors'
import NotesWidget from '@/components/listings/notes/NotesWidget'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useWatchedStatusWhileOpen } from './useWatchedStatusWhileOpen'

const NoteButtonContainer = styled.button<{ $hasNote: boolean }>`
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;

  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: box-shadow 0.2s ease;
  cursor: pointer;

  ${({ $hasNote }) =>
    $hasNote
      ? css`
          background-color: ${NOTES_AMBER_BG};
          border: 1px solid ${Colors.Semantic.SpecificFunctions.Rating.rating};
          ${Shadows.MultipleCards}
          &:hover {
            ${Shadows.Hover}
          }
        `
      : css`
          background-color: ${Colors.Semantic.Neutral.medium_grey};
          border: 1px solid ${Colors.Semantic.BStock.disabled};
          &:hover {
            ${Shadows.Hover}
          }
        `}
`

const NoteIcon = styled(FontAwesomeIcon)<{ $hasNote: boolean }>`
  color: ${({ $hasNote }) =>
    $hasNote ? NOTES_AMBER_ICON : Colors.Semantic.BStock.disabled};
  font-size: 1rem;
`

const PopoverContainer = styled.div`
  position: fixed;
  z-index: 200;
  ${notePopoverWidthCss}
  overflow-y: auto;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
`

const WatchlistNotePopover = ({
  listingId,
  listingTitle,
  note,
  dateExpires,
  listingEndTime,
  wasWatchlisted: wasWatchlistedProp,
  isOpen: isOpenProp,
  onOpenChange,
}: {
  listingId: string
  listingTitle: string
  note: string | undefined
  dateExpires: string
  /** The listing's end time (ISO), sent with the watch a new note adds. */
  listingEndTime?: string
  /**
   * Whether the listing is on the Watchlist, when the caller knows it. A card
   * can show the note of an un-watched listing while NOTES_OUTLIVE_WATCHLIST is
   * on; the widget then announces the watch a save adds. Omitted, the popover
   * reads the watched status while it is open.
   */
  wasWatchlisted?: boolean
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
}) => {
  const intl = useIntl()
  const enableListingNotes = useFlag(FeatureFlags.ENABLE_LISTING_NOTES)
  const { trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId ?? ''
  const accountId = decodedToken?.accountId ?? ''
  const [isOpenInternal, setIsOpenInternal] = useState(false)
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenInternal
  const { wasWatchlisted, isWatchedStateError } = useWatchedStatusWhileOpen({
    personId,
    listingId,
    isOpen,
    needsWatchedState: enableListingNotes,
    knownWatchlisted: wasWatchlistedProp,
  })

  const setIsOpen = useCallback(
    (val: boolean) => {
      setIsOpenInternal(val)
      onOpenChange?.(val)
    },
    [onOpenChange]
  )
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  )
  const [popoverPos, setPopoverPos] = useState<{
    // Anchored by `right` when opening leftward from the button's right edge,
    // or by `left` when flipped to open rightward instead.
    right?: number
    left?: number
    // Anchored by `top` when placed below the button, or by `bottom` when flipped
    // above it; `maxHeight` clamps it to the available space so it never clips.
    top?: number
    bottom?: number
    maxHeight: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const hasNote = !!note

  useEffect(() => {
    if (!isOpen || !buttonElement) {
      setPopoverPos(null) // oxlint-disable-line react/set-state-in-effect
      return
    }
    const rect = buttonElement.getBoundingClientRect()
    const VIEWPORT_MARGIN = NOTE_POPOVER_VIEWPORT_MARGIN
    const GAP = 8 // space between the button and the popover
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
    const spaceAbove = rect.top - VIEWPORT_MARGIN
    // Prefer opening below the button. Flip above only when there's too little
    // room below to be useful AND there's more room above. Either way, clamp
    // max-height to the available space and let the popover scroll internally, so
    // a long note can never run off the bottom of the screen when the button sits
    // low or centered in the viewport.
    const MIN_ROOM_BELOW = 280
    const placeAbove = spaceBelow < MIN_ROOM_BELOW && spaceAbove > spaceBelow
    const verticalPos = placeAbove
      ? {
          bottom: window.innerHeight - rect.top + GAP,
          maxHeight: Math.max(0, spaceAbove - GAP),
        }
      : {
          top: rect.bottom + GAP,
          maxHeight: Math.max(0, spaceBelow - GAP),
        }

    // Default anchor is the button's right edge, opening leftward. On the
    // leftmost card there isn't POPOVER_WIDTH of room to the left, so flip to
    // anchor from the button's left edge and open rightward instead, clamped
    // so it never runs past either edge of the viewport.
    const spaceLeft = rect.right - VIEWPORT_MARGIN
    const horizontalPos =
      spaceLeft < NOTE_POPOVER_WIDTH
        ? { left: clampNotePopoverLeft(rect.left, window.innerWidth) }
        : { right: window.innerWidth - rect.right }

    setPopoverPos({ ...verticalPos, ...horizontalPos })
  }, [isOpen, buttonElement])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !containerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        buttonElement?.focus()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, buttonElement, setIsOpen])

  useEffect(() => {
    if (isOpen && popoverPos) {
      popoverRef.current?.focus()
    }
  }, [isOpen, popoverPos])

  if (!enableListingNotes) return null

  return (
    <>
      <div ref={containerRef} style={{ position: 'relative' }}>
        <NoteButtonContainer
          ref={setButtonElement}
          type="button"
          $hasNote={hasNote}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!isOpen) {
              trackButtonClicked({
                buttonName: 'listing_note_icon_clicked',
                screenName,
              })
            }
            setIsOpen(!isOpen)
          }}
          aria-label={intl.formatMessage(
            { id: 'Listing.notes.editAriaLabel' },
            { listingTitle }
          )}
          aria-expanded={isOpen}
          data-testid="watchlist-note-button"
        >
          <NoteIcon $hasNote={hasNote} icon={faFileLines} />
        </NoteButtonContainer>
      </div>
      {isOpen &&
        popoverPos &&
        createPortal(
          <PopoverContainer
            ref={popoverRef}
            role="dialog"
            aria-label={intl.formatMessage(
              { id: 'Listing.notes.editAriaLabel' },
              { listingTitle }
            )}
            tabIndex={-1}
            style={{
              maxHeight: popoverPos.maxHeight,
              ...(popoverPos.left !== undefined
                ? { left: popoverPos.left }
                : { right: popoverPos.right }),
              ...(popoverPos.top !== undefined
                ? { top: popoverPos.top }
                : { bottom: popoverPos.bottom }),
            }}
          >
            <NotesWidget
              listingId={listingId}
              personId={personId}
              accountId={accountId}
              dateExpires={dateExpires}
              note={note}
              wasWatchlisted={wasWatchlisted}
              isWatchedStateError={isWatchedStateError}
              listingEndTime={listingEndTime}
              defaultMode={note ? 'view' : 'edit'}
              onEditComplete={() => setIsOpen(false)}
            />
          </PopoverContainer>,
          document.body
        )}
    </>
  )
}

export default WatchlistNotePopover
